import { useState, useCallback, useEffect } from "react";
import { IEventSummary, ICourseSummary, ISession, IQuery } from "@eagerdog/interfaces";
import { apiService } from "src/services";
import moment from "moment";

export interface ClubCalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  allDay: boolean;
  extendedProps: {
    type: "event" | "course";
    handle: string;
    showTypes?: string[];
    category?: string;
    /** For course sessions — the actual course _id used for API calls */
    sourceId?: string;
    /** 1-based index of this session within the course */
    sessionIndex?: number;
    /** Total number of sessions in the course */
    sessionTotal?: number;
    /** First instructor's full name (if any) */
    instructor?: string;
    /** Number of enrolled registrations for this session */
    enrolled?: number;
    /** Maximum capacity for this session */
    capacity?: number;
    // Tooltip fields — add more here as needed
    clubName?: string;
    city?: string;
    state?: string;
    country?: string;
    status?: string;
  };
}

interface UseClubCalendarDataOptions {
  clubId: string;
  isAdmin?: boolean;
}

interface UseClubCalendarDataReturn {
  events: ClubCalendarEvent[];
  loading: boolean;
  fetchRange: (start: Date, end: Date) => void;
  /** Immediately shift dates in React state for course sessions in [fromIdx, toIdx). */
  shiftCourseSessionDates: (courseId: string, fromIdx: number, toIdx: number | undefined, deltaMs: number) => void;
}

export function useClubCalendarData({
  clubId,
  isAdmin = false,
}: UseClubCalendarDataOptions): UseClubCalendarDataReturn {
  const [events, setEvents] = useState<ClubCalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchRange = useCallback(
    async (start: Date, end: Date) => {
      setLoading(true);
      try {
        const startIso = moment(start).toISOString();
        const endIso = moment(end).toISOString();

        const eventQuery: IQuery = {
          $limit: 500,
          $skip: 0,
          sort: [{ attribute_name: "start_date", sort: "asc" }],
          query: {
            $and: [
              { attribute_name: "start_date", attribute_value: { operator: "$gte" as const, value: startIso } },
              { attribute_name: "end_date",   attribute_value: { operator: "$lte" as const, value: endIso } },
            ],
          },
        };

        const courseQuery: IQuery = {
          $limit: 500,
          $skip: 0,
          sort: [{ attribute_name: "start_date", sort: "asc" }],
          query: {
            $and: [
              // Fetch any course that overlaps the current view window
              { attribute_name: "start_date", attribute_value: { operator: "$lte" as const, value: endIso } },
              { attribute_name: "end_date",   attribute_value: { operator: "$gte" as const, value: startIso } },
              { attribute_name: "club_id",    attribute_value: { operator: "$eq",           value: clubId } },
            ],
          },
        };

        const eventsPromise = isAdmin
          ? apiService.getClubEvents(clubId, eventQuery)
          : apiService.getPublicClubEvents(clubId, eventQuery);

        const coursesPromise = isAdmin
          ? apiService.getPrivateCourses(courseQuery, clubId)
          : apiService.getCourses(courseQuery);

        const [clubEvents, clubCourses] = await Promise.all([
          eventsPromise,
          coursesPromise,
        ]);

        const calEvents: ClubCalendarEvent[] = [
          ...clubEvents.map((e: IEventSummary) => ({
            id: `event-${e._id}`,
            title: e.title || "Event",
            start: moment(e.start_date).toISOString(),
            end: moment(e.end_date || e.start_date).add(1, "day").startOf("day").toISOString(),
            allDay: true,
            extendedProps: {
              type: "event" as const,
              handle: e.handle,
              showTypes: (e.show_types || []).map((st: any) => st.show_type),
              status: e.status,
              clubName: e.club?.club_name,
              city: e.address?.city || e.club?.address?.city,
              state: e.address?.state || e.club?.address?.state,
              country: e.address?.country || e.club?.address?.country,
            },
          })),
          ...clubCourses.flatMap((c: ICourseSummary) =>
            (c.schedule || [])
              // Only show sessions that fall within the current view window
              .filter((session: ISession) =>
                moment(session.start_date).isBefore(moment(end)) &&
                moment(session.end_date).isAfter(moment(start))
              )
              .map((session: ISession, idx: number, visibleSessions: ISession[]) => {
                const firstInstructor = session.instructors?.[0];
                const instructor = firstInstructor
                  ? `${firstInstructor.first_name} ${firstInstructor.last_name}`.trim()
                  : undefined;

                // Determine this session's 1-based position within ALL sessions (not just visible)
                const allSessions = c.schedule || [];
                const globalIdx = allSessions.indexOf(session);
                const sessionIndex = globalIdx >= 0 ? globalIdx + 1 : idx + 1;

                return {
                  id: `course-${c._id}-s${globalIdx}`,
                  title: c.title || "Course",
                  start: moment(session.start_date).toISOString(),
                  end: moment(session.end_date).toISOString(),
                  allDay: false,
                  extendedProps: {
                    type: "course" as const,
                    handle: c.handle,
                    category: c.category,
                    sourceId: c._id,
                    sessionIndex,
                    sessionTotal: allSessions.length,
                    instructor,
                    clubName: c.club?.club_name,
                    city: c.club?.address?.city,
                    state: c.club?.address?.state,
                    country: c.club?.address?.country,
                    status: c.status,
                  },
                };
              })
          ),
        ];

        setEvents(calEvents);
      } catch (err) {
        console.error("[useClubCalendarData] Failed to fetch:", err);
      } finally {
        setLoading(false);
      }
    },
    [clubId, isAdmin]
  );

  // Initial load — fetch a wide window centered on today
  useEffect(() => {
    const start = moment().subtract(2, "months").startOf("month").toDate();
    const end   = moment().add(10, "months").endOf("month").toDate();
    fetchRange(start, end);
  }, [fetchRange]);

  const shiftCourseSessionDates = useCallback(
    (courseId: string, fromIdx: number, toIdx: number | undefined, deltaMs: number) => {
      setEvents(prev => prev.map(event => {
        if (event.extendedProps.type !== "course") return event;
        if (event.extendedProps.sourceId !== courseId) return event;
        const evtIdx = (event.extendedProps.sessionIndex ?? 1) - 1;
        if (evtIdx < fromIdx) return event;
        if (toIdx !== undefined && evtIdx >= toIdx) return event;
        return {
          ...event,
          start: new Date(new Date(event.start).getTime() + deltaMs).toISOString(),
          end:   new Date(new Date(event.end).getTime()   + deltaMs).toISOString(),
        };
      }));
    },
    []
  );

  return { events, loading, fetchRange, shiftCourseSessionDates };
}
